import { useEffect, useState } from 'react';
import { useRole } from '@/stores/identity';
import { formatEventType, listAuditEvents } from '@/lib/audit';
import type { AuditEvent } from '@/lib/types';
import { humanize } from '@/lib/errors';
import { formatDateTime } from '@/lib/dates';

/**
 * /audit — owner-only audit log viewer.
 *
 * Paginated read against audit_events_for_org(). The RPC also gates on
 * owner role server-side, so this is a defence-in-depth check (we still
 * RouteGuard at the App.tsx level).
 */
const PAGE_SIZE = 50;

export default function Audit() {
  const role = useRole();
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    listAuditEvents(PAGE_SIZE, offset)
      .then((page) => {
        if (cancelled) return;
        setRows(page.rows);
        setTotal(page.total);
        setStatus('loaded');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(humanize(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [offset]);

  if (role !== 'owner' && role !== 'system_admin') {
    return (
      <div className="container mx-auto py-8 px-4">
        <p className="text-sm text-muted-foreground">
          The audit log is owner-only.
        </p>
      </div>
    );
  }

  const pageStart = offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
          <p className="text-sm text-muted-foreground">
            Every mutation in your organization, newest first. Click a row for the full payload.
          </p>
        </div>

        {status === 'loading' && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {status === 'error' && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        {status === 'loaded' && rows.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No audit events recorded.
          </div>
        )}

        {status === 'loaded' && rows.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Event</th>
                    <th className="px-3 py-2 text-left">Actor role</th>
                    <th className="px-3 py-2 text-left">Target</th>
                    <th className="px-3 py-2 text-left">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isOpen = expanded === row.event_id;
                    return (
                      <>
                        <tr
                          key={row.event_id}
                          className="border-t border-border hover:bg-muted/20 cursor-pointer"
                          onClick={() => setExpanded(isOpen ? null : row.event_id)}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                            {formatDateTime(row.occurred_at)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                            {formatEventType(row.event_type)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                            {row.actor_role_at_time ?? '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                            {row.target_type ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-xs">{row.summary}</td>
                        </tr>
                        {isOpen && (
                          <tr key={`${row.event_id}-payload`} className="border-t border-border bg-muted/10">
                            <td colSpan={5} className="px-3 py-3">
                              <pre className="overflow-x-auto text-[11px] leading-snug">
                                {JSON.stringify(row.payload, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Showing {pageStart}–{pageEnd} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50"
                >
                  ← Newer
                </button>
                <button
                  type="button"
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={pageEnd >= total}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50"
                >
                  Older →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
